import React from "react";
import { X } from "lucide-react";
import { useStore } from "../store";
import {
  lpaStatusStyle,
  auditorTypeStyle,
  buildPreAuditLogbook,
  actionBadgeColor,
} from "../data/eightDData";
import { useLpas, useIncidents, useEightDs, useFmeas } from "../api/sitroom";

const SectionTitle = ({ children }) => (
  <div
    className="mt-5 mb-2.5"
    style={{
      fontFamily: "'DM Mono', monospace",
      fontSize: 10,
      letterSpacing: "1.6px",
      color: "#9198b0",
      textTransform: "uppercase",
    }}
  >
    {children}
  </div>
);

const Pill = ({ children, bg, border, color }) => (
  <span
    style={{
      background: bg,
      border: `1px solid ${border}`,
      color,
      fontFamily: "'DM Mono', monospace",
      fontSize: 9,
      padding: "2px 7px",
      borderRadius: 3,
      letterSpacing: "1px",
      fontWeight: 500,
    }}
  >
    {children}
  </span>
);

const LinkCard = ({ id, title, status, onClick, accent }) => (
  <button
    onClick={onClick}
    className="w-full text-left rounded-md p-3"
    style={{
      background: "#0d0f18",
      border: `1px solid ${accent}33`,
      transition: "border-color 150ms",
    }}
  >
    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: accent }}>
      {id}
    </div>
    <div style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 12, color: "#f0f2f8", marginTop: 2 }}>
      {title}
    </div>
    {status && (
      <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#c5cad8", marginTop: 2, letterSpacing: "0.5px" }}>
        {status}
      </div>
    )}
  </button>
);

export const LPAPanel = () => {
  const isOpen = useStore((s) => s.isLpaPanelOpen);
  const closePanel = useStore((s) => s.closeLpaPanel);
  const selectedId = useStore((s) => s.selectedLpaId);
  const openIncident = useStore((s) => s.openPanel);
  const openEightD = useStore((s) => s.openEightDPanel);
  const openFmea = useStore((s) => s.openFmeaPanel);

  const { data: lpaRequests = [] } = useLpas();
  const { data: incidents = [] } = useIncidents();
  const { data: eightDReports = [] } = useEightDs();
  const { data: fmeaSuggestions = [] } = useFmeas();

  const r = lpaRequests.find((x) => x.id === selectedId) || lpaRequests[0];
  if (!isOpen || !r) return null;

  const inc = incidents.find((i) => i.id === r.sourceIncident);
  const eight = eightDReports.find((e) => e.sourceIncident === r.sourceIncident);
  const fmea = fmeaSuggestions.find((f) => f.sourceIncident === r.sourceIncident);
  const at = auditorTypeStyle(r.auditorType);
  const st = lpaStatusStyle(r.status);
  const logbook = buildPreAuditLogbook(inc);
  if (eight) {
    logbook.push({
      time: eight.lastUpdated || "",
      actor: eight.teamChampion || "—",
      action: "8D OPENED",
      detail: `${eight.id} — ${eight.title}`,
    });
  }

  return (
    <>
      <div
        className="fixed inset-0 z-[400] backdrop-enter"
        style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)" }}
        onClick={closePanel}
        data-testid="lpa-panel-backdrop"
      />
      <aside
        className="fixed top-0 right-0 bottom-0 z-[401] panel-enter flex flex-col"
        style={{ width: "min(600px, 100vw)", background: "#0d0f18", borderLeft: "1px solid rgba(255,255,255,0.1)" }}
        data-testid="lpa-panel"
      >
        <header className="relative p-5 pb-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
          <button
            onClick={closePanel}
            className="absolute right-4 top-4 flex items-center justify-center rounded-md hover:bg-white/5"
            style={{ width: 28, height: 28, border: "1px solid rgba(255,255,255,0.1)", color: "#9aa1b3" }}
            data-testid="close-lpa-panel-btn"
            aria-label="Close"
          >
            <X size={14} />
          </button>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#c89adb" }}>{r.id}</span>
            <Pill bg="rgba(142,68,173,0.18)" border="rgba(142,68,173,0.5)" color="#c89adb">
              {r.standard}
            </Pill>
            <Pill bg={at.bg} border={at.border} color={at.color}>{at.label}</Pill>
            <Pill bg={st.bg} border={st.border} color={st.color}>{st.label}</Pill>
          </div>
          <div style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 16, color: "#f0f2f8", paddingRight: 40 }}>
            Layered Process Audit — {r.standard}
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-5 pt-3">
          <SectionTitle>Request Details</SectionTitle>
          <div className="rounded-md p-3 space-y-2" style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.12)" }}>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#cfd1d8" }}>
              <strong style={{ color: "#c5cad8" }}>Routing:</strong> {r.severity.toUpperCase()} + {r.standard} → {at.label} per routing rules
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#cfd1d8" }}>
              <strong style={{ color: "#c5cad8" }}>Auditor:</strong> {r.auditor}
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#cfd1d8" }}>
              <strong style={{ color: "#c5cad8" }}>Scheduled:</strong> {r.scheduledDate}
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#cfd1d8" }}>
              <strong style={{ color: "#c5cad8" }}>Requested:</strong> {r.requestedBy} · {r.requestedAt}
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#cfd1d8", lineHeight: 1.5 }}>
              <strong style={{ color: "#c5cad8" }}>Scope:</strong> {r.scope}
            </div>
          </div>

          {inc && (
            <>
              <SectionTitle>Source Incident</SectionTitle>
              <div className="rounded-md p-3" style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.12)" }}>
                <button onClick={() => openIncident(inc.id)} style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#9ec3ec", textDecoration: "underline" }}>
                  {inc.id}
                </button>
                <div style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 13, color: "#f0f2f8", marginTop: 4 }}>
                  {inc.title}
                </div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#c5cad8", marginTop: 3 }}>
                  {inc.parties} · {inc.impact} · {inc.loss}
                </div>
              </div>
            </>
          )}

          <SectionTitle>Pre-Audit Logbook</SectionTitle>
          <div className="rounded-md p-3" style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.12)" }}>
            {logbook.map((e, i) => {
              const c = actionBadgeColor(e.action);
              return (
                <div key={i} className="relative pl-6 pb-3 last:pb-0">
                  {i < logbook.length - 1 && (
                    <span className="absolute left-[3.5px] top-3 bottom-0 w-px" style={{ background: "rgba(255,255,255,0.08)" }} />
                  )}
                  <span
                    className="absolute left-0 top-1.5 rounded-full"
                    style={{ width: 8, height: 8, background: c, border: `2px solid ${c}`, boxShadow: "0 0 0 3px rgba(0,0,0,0.6)" }}
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0" }}>
                      [{e.time}]
                    </span>
                    <span style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 11, color: "#f0f2f8" }}>
                      {e.actor}
                    </span>
                    <Pill bg={`${c}22`} border={`${c}80`} color={c}>{e.action}</Pill>
                  </div>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#cfd1d8", marginTop: 3, lineHeight: 1.5 }}>
                    {e.detail}
                  </div>
                </div>
              );
            })}
            {logbook.length === 0 && (
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#9198b0" }}>No logbook entries.</div>
            )}
          </div>

          <SectionTitle>Linked Documents</SectionTitle>
          <div className="grid grid-cols-1 gap-2">
            {inc && (
              <LinkCard
                id={inc.id}
                title={inc.title}
                status={`Severity ${inc.severity.toUpperCase()} · ${inc.status}`}
                accent="#185FA5"
                onClick={() => openIncident(inc.id)}
              />
            )}
            {eight && (
              <LinkCard
                id={eight.id}
                title={eight.title}
                status={`Step ${eight.currentStep} · ${eight.status}`}
                accent="#185FA5"
                onClick={() => openEightD(eight.id)}
              />
            )}
            {fmea && (
              <LinkCard
                id={fmea.id}
                title={fmea.failureMode}
                status={`RPN ${fmea.severity * fmea.occurrence * fmea.detection} · ${fmea.status}`}
                accent="#8e44ad"
                onClick={() => openFmea(fmea.id)}
              />
            )}
          </div>

          <SectionTitle>Auditor Notes</SectionTitle>
          <textarea
            rows={3}
            placeholder="Auditor can add pre-audit notes here…"
            className="w-full rounded-md px-3 py-2"
            style={{
              background: "#0d0f18",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#f0f2f8",
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              resize: "vertical",
            }}
            data-testid="lpa-auditor-notes"
          />

          <SectionTitle>Notification Log</SectionTitle>
          <div className="rounded-md p-3" style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.12)" }}>
            {r.notifications.map((n, i) => (
              <div key={i} style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#cfd1d8", marginBottom: 4 }}>
                <span style={{ color: "#9198b0" }}>[{n.at}]</span> {n.channel} sent to <span style={{ color: "#9ec3ec" }}>{n.target}</span>
              </div>
            ))}
          </div>
        </div>

        <footer
          className="flex items-center gap-2 p-3 flex-wrap"
          style={{ background: "#0d0f18", borderTop: "1px solid rgba(255,255,255,0.12)" }}
        >
          <button
            className="px-3 py-2 rounded-md"
            style={{
              background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.5)",
              color: "#ff7e7c", fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 11,
            }}
            data-testid="lpa-cancel-btn"
          >
            Cancel Request
          </button>
          <button
            className="px-3 py-2 rounded-md"
            style={{
              background: "transparent", border: "1px solid rgba(255,255,255,0.12)",
              color: "#cfd1d8", fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 600, fontSize: 11,
            }}
            data-testid="lpa-download-logbook-btn"
          >
            Download Logbook PDF
          </button>
          <div className="flex-1" />
          {(r.status === "scheduled" || r.status === "in-progress") && (
            <button
              className="px-3 py-2 rounded-md"
              style={{
                background: "rgba(39,174,96,0.14)", border: "1px solid rgba(39,174,96,0.5)",
                color: "#4ed084", fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 11,
              }}
              data-testid="lpa-complete-btn"
            >
              ✓ Mark as Completed
            </button>
          )}
        </footer>
      </aside>
    </>
  );
};
